import { useMemo } from 'react';

/** Material UI Packages. */
import { Box, Checkbox, Chip, FormControlLabel, Typography } from '@mui/material';

/** Internal Components. */
import { columnOptionCategory, columnOption } from 'shared/config/constantsData';

const CHIP_STYLES = {
    base: {
        mr: '5px',
        '&.MuiChip-outlined:hover': {
            boxShadow: 'none',
        },
    },
    selected: {
        color: '#ffffff',
        backgroundColor: '#00647e',
        '&.MuiChip-outlined:hover': {
            backgroundColor: '#2488a2',
            color: '#ffffff',
        },
    },
    unselected: {
        color: '#c1c1c1',
        backgroundColor: '#ffffff',
        '&.MuiChip-outlined:hover': {
            backgroundColor: '#ffffff',
            color: '#000000',
        },
    },
    error: {
        color: '#d32f2f',
    },
};

const ERROR_MESSAGE = 'Please fill out one of Columns below.';
const ALL_COLUMNS_LABEL = 'All columns are selected.';

/** Column selection component. */
export default function DataExportColumn(props) {
    /** Get props from DataExport.js. */
    const { filterResult, setFilterResult, columnsCheck, setColumnsCheck, isError } = props;

    const { columnsList, shipmentType } = filterResult;

    /** Get available columns only. */
    const availableColumns = useMemo(() => {
        return columnOption
            .flat()
            .filter((col) => col.index.includes(shipmentType))
            .map((col) => col.value);
    }, [shipmentType]);

    /** Get filtered column category with options. */
    const filteredColumnOptions = useMemo(() => {
        return columnOptionCategory.map((category, index) => ({
            category,
            options: columnOption[index].filter(
                (option) => option.value !== 'Status' && option.index.includes(shipmentType)
            ),
        }));
    }, [shipmentType]);

    /** Sort columns by category order. */
    const sortColumnsByCategoryOrder = (columnsList, shipmentType) => {
        const sorted = [];
        const preventDuplicate = new Set();

        columnOption.forEach((categoryArr) => {
            if (Array.isArray(categoryArr)) {
                categoryArr.forEach((option) => {
                    if (
                        columnsList.includes(option.value) &&
                        !preventDuplicate.has(option.value) &&
                        option.index.includes(shipmentType)
                    ) {
                        sorted.push(option.value);
                        preventDuplicate.add(option.value);
                    }
                });
            }
        });

        return sorted;
    };

    /** Get chip styles. */
    const getChipStyles = (isSelected) => {
        const baseStyles = CHIP_STYLES.base;
        const stateStyles = isSelected ? CHIP_STYLES.selected : CHIP_STYLES.unselected;
        const errorStyles = isError ? CHIP_STYLES.error : {};

        return {
            ...baseStyles,
            ...stateStyles,
            ...errorStyles,
        };
    };

    /** Handle column selection. */
    const handleColumnSelection = (option) => {
        const isAlreadySelected = columnsList.includes(option.value);

        const updatedColumns = isAlreadySelected
            ? columnsList.filter((item) => item !== option.value)
            : [...columnsList, option.value];

        const sortedColumns = sortColumnsByCategoryOrder(updatedColumns, shipmentType);

        setFilterResult((prev) => ({
            ...prev,
            columnsList: sortedColumns,
        }));

        // Update checkbox state
        const isAllSelected = sortedColumns.length === availableColumns.length && sortedColumns.length > 0;
        setColumnsCheck(isAllSelected);
    };

    /** Handle select all columns. */
    const handleSelectAllColumns = () => {
        if (!columnsCheck) {
            // Select all available columns
            const sortedValues = sortColumnsByCategoryOrder(availableColumns, shipmentType);
            setFilterResult((prev) => ({
                ...prev,
                columnsList: sortedValues,
            }));
        } else {
            // Clear all columns
            setFilterResult((prev) => ({
                ...prev,
                columnsList: [],
            }));
        }

        setColumnsCheck((prev) => !prev);
    };

    /** Render error message. */
    const renderErrorMessage = () => {
        if (!isError) return null;

        return (
            <Typography fontSize="13px" fontWeight="bold" fontStyle="italic" color="error">
                {ERROR_MESSAGE}
            </Typography>
        );
    };

    /** Render category section. */
    const renderCategorySection = (categoryData) => {
        const { category, options } = categoryData;

        return (
            <Box key={category} sx={{ mb: '10px' }}>
                <Typography fontSize="11px" sx={{ mb: '5px' }}>
                    {category}
                </Typography>
                {options.map((option) => {
                    const isSelected = columnsList.includes(option.value);

                    return (
                        <Chip
                            key={option.value}
                            label={option.value}
                            variant="outlined"
                            onClick={() => handleColumnSelection(option)}
                            color={isError ? 'error' : ''}
                            sx={getChipStyles(isSelected)}
                        />
                    );
                })}
            </Box>
        );
    };

    /** Render select all checkbox. */
    const renderSelectAllCheckbox = () => (
        <FormControlLabel
            sx={{ height: '25px' }}
            control={<Checkbox checked={columnsCheck} onChange={handleSelectAllColumns} size="small" />}
            label={<Typography sx={{ fontSize: '11px', color: '#003a52' }}>{ALL_COLUMNS_LABEL}</Typography>}
        />
    );

    return (
        <Box>
            {renderErrorMessage()}
            {filteredColumnOptions.map(renderCategorySection)}
            {renderSelectAllCheckbox()}
        </Box>
    );
}
